import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  Alert,
  Divider,
  Collapse,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CodeIcon from '@mui/icons-material/Code';
import ViewListIcon from '@mui/icons-material/ViewList';
import DataObjectIcon from '@mui/icons-material/DataObject';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import type { CriteriaBuilderProps, CriteriaCondition, CriteriaMode } from './types';
import { CriteriaFormRow } from './CriteriaFormRow';
import {
  conditionsToDsl,
  dslToConditions,
  conditionsToJson,
  jsonToConditions,
  createEmptyCondition,
  validateCondition,
} from './utils';

export const CriteriaBuilder = ({
  entityType,
  value = [],
  onChange,
  onDslChange,
  disabled = false,
  label = 'Criteria',
}: CriteriaBuilderProps) => {
  const [mode, setMode] = useState<CriteriaMode>('form');
  const [conditions, setConditions] = useState<CriteriaCondition[]>(value);
  const [dslText, setDslText] = useState('');
  const [jsonText, setJsonText] = useState('');
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [showPreview, setShowPreview] = useState(true);

  // Sync internal state with prop value (using JSON comparison for deep equality)
  useEffect(() => {
    const valueJson = JSON.stringify(value);
    const conditionsJson = JSON.stringify(conditions);
    if (valueJson !== conditionsJson) {
      setConditions(value);
      setDslText(conditionsToDsl(value));
      setJsonText(conditionsToJson(value));
    }
  }, [value, conditions]);

  // Update DSL preview when conditions change in form mode
  useEffect(() => {
    if (mode === 'form') {
      setDslText(conditionsToDsl(conditions));
      setJsonText(conditionsToJson(conditions));
    }
  }, [conditions, mode]);

  // Validate conditions
  useEffect(() => {
    const errors: string[] = [];
    for (const condition of conditions) {
      const error = validateCondition(condition, entityType);
      if (error) {
        errors.push(error);
      }
    }
    setValidationErrors(errors);
  }, [conditions, entityType]);

  // Notify parent of DSL changes
  useEffect(() => {
    if (onDslChange) {
      onDslChange(dslText);
    }
  }, [dslText, onDslChange]);

  const handleModeChange = (_: React.MouseEvent<HTMLElement>, newMode: CriteriaMode | null) => {
    if (!newMode) return;

    // Convert data when switching modes
    if (newMode === 'form' && mode === 'dsl') {
      const parsed = dslToConditions(dslText);
      setConditions(parsed);
      onChange(parsed);
    } else if (newMode === 'form' && mode === 'json') {
      const parsed = jsonToConditions(jsonText);
      setConditions(parsed);
      onChange(parsed);
    } else if (newMode === 'dsl' && mode === 'form') {
      setDslText(conditionsToDsl(conditions));
    } else if (newMode === 'dsl' && mode === 'json') {
      const parsed = jsonToConditions(jsonText);
      setDslText(conditionsToDsl(parsed));
    } else if (newMode === 'json' && mode === 'form') {
      setJsonText(conditionsToJson(conditions));
    } else if (newMode === 'json' && mode === 'dsl') {
      const parsed = dslToConditions(dslText);
      setJsonText(conditionsToJson(parsed));
    }

    setMode(newMode);
  };

  const handleAddCondition = useCallback(() => {
    const newCondition = createEmptyCondition(entityType);
    const newConditions = [...conditions, newCondition];
    setConditions(newConditions);
    onChange(newConditions);
  }, [conditions, entityType, onChange]);

  const handleUpdateCondition = useCallback(
    (index: number, updatedCondition: CriteriaCondition) => {
      const newConditions = [...conditions];
      newConditions[index] = updatedCondition;
      setConditions(newConditions);
      onChange(newConditions);
    },
    [conditions, onChange]
  );

  const handleRemoveCondition = useCallback(
    (index: number) => {
      const newConditions = conditions.filter((_, i) => i !== index);
      setConditions(newConditions);
      onChange(newConditions);
    },
    [conditions, onChange]
  );

  const handleDslChange = useCallback(
    (text: string) => {
      setDslText(text);
      // Parse and update conditions
      const parsed = dslToConditions(text);
      setConditions(parsed);
      onChange(parsed);
    },
    [onChange]
  );

  const handleJsonChange = useCallback(
    (text: string) => {
      setJsonText(text);
      // Parse and update conditions
      const parsed = jsonToConditions(text);
      setConditions(parsed);
      onChange(parsed);
    },
    [onChange]
  );

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="medium">
          {label}
        </Typography>
        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={handleModeChange}
          size="small"
          disabled={disabled}
        >
          <ToggleButton value="form">
            <ViewListIcon fontSize="small" sx={{ mr: 0.5 }} />
            Form
          </ToggleButton>
          <ToggleButton value="dsl">
            <CodeIcon fontSize="small" sx={{ mr: 0.5 }} />
            DSL
          </ToggleButton>
          <ToggleButton value="json">
            <DataObjectIcon fontSize="small" sx={{ mr: 0.5 }} />
            JSON
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {validationErrors.map((error, i) => (
            <div key={i}>{error}</div>
          ))}
        </Alert>
      )}

      {/* Form Mode */}
      {mode === 'form' && (
        <Box>
          {conditions.length === 0 ? (
            <Typography color="textSecondary" sx={{ mb: 2, fontStyle: 'italic' }}>
              No conditions defined. Click "Add Condition" to start.
            </Typography>
          ) : (
            conditions.map((condition, index) => (
              <CriteriaFormRow
                key={condition.id}
                condition={condition}
                entityType={entityType}
                onUpdate={(updated) => handleUpdateCondition(index, updated)}
                onRemove={() => handleRemoveCondition(index)}
                disabled={disabled}
              />
            ))
          )}

          <Button
            startIcon={<AddIcon />}
            onClick={handleAddCondition}
            disabled={disabled}
            size="small"
            sx={{ mt: 1 }}
          >
            Add Condition
          </Button>
        </Box>
      )}

      {/* DSL Mode */}
      {mode === 'dsl' && (
        <TextField
          fullWidth
          multiline
          rows={6}
          value={dslText}
          onChange={(e) => handleDslChange(e.target.value)}
          disabled={disabled}
          placeholder={`Enter DSL expressions (one per line):
career.domestic.ipl.totals.runs >= 1000
career.domestic.ipl.teams CONTAINS 'MI'`}
          sx={{
            '& .MuiInputBase-input': {
              fontFamily: 'monospace',
              fontSize: '0.875rem',
            },
          }}
        />
      )}

      {/* JSON Mode */}
      {mode === 'json' && (
        <TextField
          fullWidth
          multiline
          rows={8}
          value={jsonText}
          onChange={(e) => handleJsonChange(e.target.value)}
          disabled={disabled}
          placeholder={`[
  {
    "path": "career.domestic.ipl.totals.runs",
    "operator": "gte",
    "value": 1000
  }
]`}
          sx={{
            '& .MuiInputBase-input': {
              fontFamily: 'monospace',
              fontSize: '0.875rem',
            },
          }}
        />
      )}

      {/* DSL Preview (for form mode) */}
      {mode === 'form' && conditions.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
            }}
            onClick={() => setShowPreview(!showPreview)}
          >
            <Typography variant="body2" color="textSecondary">
              DSL Preview
            </Typography>
            <IconButton size="small">
              {showPreview ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Collapse in={showPreview}>
            <Paper
              variant="outlined"
              sx={{
                p: 1,
                mt: 1,
                backgroundColor: '#f5f5f5',
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                whiteSpace: 'pre-wrap',
              }}
            >
              {dslText || 'No conditions'}
            </Paper>
          </Collapse>
        </>
      )}
    </Paper>
  );
};
